import React, { useState } from "react";
import KuikaCheckbox from "../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

interface IStatefulProps {
  initial?: boolean;
  label?: string;
  align?: "center" | "top";
  labelPosition?: "right" | "left";
  disabled?: boolean;
}

const StatefulCheckbox: React.FC<IStatefulProps> = ({ initial = false, ...rest }) => {
  const [checked, setChecked] = useState<boolean>(initial);
  return (
    <KuikaCheckbox
      checked={checked}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChecked(e.target.checked)}
      {...rest}
    />
  );
};

const CHILD_LABELS: string[] = ["Screens", "Actions", "Data sources"];

// A select-all box over three children: it shows the bar while some are ticked and draws the tick
// when the last one is.
const SelectAllDemo: React.FC = (): JSX.Element => {
  const [ticked, setTicked] = useState<string[]>(["Screens"]);
  const allTicked: boolean = ticked.length === CHILD_LABELS.length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-s)" }}>
      <KuikaCheckbox
        label="Export everything"
        checked={allTicked}
        indeterminate={ticked.length > 0}
        onChange={(): void => setTicked(allTicked ? [] : CHILD_LABELS)}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-s)", paddingLeft: "var(--gap-xl)" }}>
        {CHILD_LABELS.map((label: string): React.ReactNode => (
          <KuikaCheckbox
            key={label}
            label={label}
            checked={ticked.includes(label)}
            onChange={(): void =>
              setTicked(
                ticked.includes(label) ? ticked.filter((item: string): boolean => item !== label) : [...ticked, label]
              )
            }
          />
        ))}
      </div>
    </div>
  );
};

export const CheckboxStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Checkbox"
      componentName="KuikaCheckbox"
      sourcePath="ui-kit/kuika-checkbox"
      description="Standalone binary control with label slots, alignment and disabled state. Ticking draws the tick in; a partial selection shows a bar. Focus ring on keyboard focus; 32px hit area; presses in."
    >
      <VariantGrid
        label="state · checked"
        rows={["enabled", "disabled"]}
        cols={["unchecked", "checked"]}
        cellMinWidth={120}
        render={(state: string, checked: string): React.ReactNode => (
          <KuikaCheckbox
            label="Remember me"
            checked={checked === "checked"}
            disabled={state === "disabled"}
            onChange={noop}
          />
        )}
      />
      <SpecRow label="Draw" caption="only a change draws — boxes that open ticked stay still" align="start">
        <StatefulCheckbox label="Tick me" />
        <SelectAllDemo />
      </SpecRow>
      <SpecRow label="Label position">
        <StatefulCheckbox initial label="Right label (default)" labelPosition="right" />
        <StatefulCheckbox initial label="Left label" labelPosition="left" />
      </SpecRow>
      <SpecRow label="Vertical alignment" align="start">
        <StatefulCheckbox initial label="Center align — single line" align="center" />
        <StatefulCheckbox
          initial
          label="Top align — useful when the label wraps to multiple lines like this one"
          align="top"
        />
      </SpecRow>
      <SpecRow label="Without label">
        <KuikaCheckbox checked onChange={noop} />
        <KuikaCheckbox onChange={noop} />
        <KuikaCheckbox checked disabled onChange={noop} />
      </SpecRow>
    </ShowcaseFrame>
  );
};
